// Template revision history (ADR 0034 / kondo-parity 1.13).
//
// Thin TanStack Query reader for `template_revisions`. The actual
// revisions are inserted by a Postgres BEFORE-UPDATE trigger on
// `templates`, so clients only ever *read* here — there's no
// mutation path.
//
// "Restore" isn't a new concept — it's just a regular
// `updateTemplate({ name, body })` that happens to use a past
// revision's values. The trigger then captures the current state
// into a new revision, so the chain is preserved and nothing is
// lost.

import { useQuery, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import type { Row } from '../lib/db-types'

/** Narrowed from `Row<'template_revisions'>` — the UI doesn't need
 * `template_id` / `team_id` because the hook is already keyed by
 * templateId. */
export type TemplateRevision = Pick<
  Row<'template_revisions'>,
  'id' | 'name' | 'body' | 'edited_by' | 'created_at'
>

export const templateRevisionsKey = (
  userId: string | undefined,
  templateId: string | null,
): QueryKey => ['template_revisions', userId ?? 'anon', templateId ?? 'none']

export function useTemplateRevisions(templateId: string | null) {
  const { user } = useAuth()
  const key = templateRevisionsKey(user?.id, templateId)

  const query = useQuery<TemplateRevision[]>({
    queryKey: key,
    // Disable when no template is selected — the Templates page gates
    // rendering of the history panel on `selected`, so this query
    // fires only when there's actually something to fetch.
    enabled: !!user && !!templateId,
    queryFn: async () => {
      if (!templateId) return []
      const { data, error } = await supabase
        .from('template_revisions')
        .select('id, name, body, edited_by, created_at')
        .eq('template_id', templateId)
        .order('created_at', { ascending: false })
        // Bound the list — users don't need to scroll through every edit
        // they ever made. Matches the "last N" pruning story we'll
        // formalise if history volume ever becomes a concern.
        .limit(50)
      if (error) throw new Error(error.message)
      return (data ?? []) as TemplateRevision[]
    },
  })

  return {
    revisions: query.data ?? [],
    loading: !!user && !!templateId && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
  }
}
